import React, { forwardRef, } from 'react';
import styled, { css } from 'styled-components';
import { spacing, radius, themeLight, icons } from '../../theme/index';
import { Loader } from '../../loader/index';
import { Text, TextVariant } from '../../text/index';
const defaultState = {
    isLoading: false,
    disabled: false,
    size: 'medium',
    variant: 'signature',
    width: 'content',
    isNotClickable: false,
};
const resetButtonStyles = css `
  border: none;
  margin: 0;
  padding: 0;
  width: auto;
  overflow: visible;
  background: transparent;
  color: inherit;
  font: inherit;
  line-height: normal;
  touch-action: manipulation;
  appearance: none !important;
`;
const notDisabled = ':not([disabled]):not([data-disabled])';
const commonStyles = css `
  ${resetButtonStyles}
  display: inline-flex;
  justify-content: center;
  align-items: center;
  text-decoration: none;
  border-radius: ${radius.s};
  transition: background 0.2s ease, border-color 0.2s ease;
  cursor: pointer;
  width: fit-content;
  box-sizing: border-box;

  :focus,
  :focus-visible {
    outline: solid 1px;
  }

  &[disabled],
  &[data-disabled],
  &:disabled {
    border: none;
    cursor: not-allowed;
    color: ${({ theme }) => theme.typography
    ? theme.typography.disabledText
    : themeLight.typography.disabledText};
    background: ${({ theme }) => theme.componentColors
    ? theme.componentColors.cta.disabled
    : themeLight.componentColors.cta.disabled};

    &:visited {
      color: ${({ theme }) => theme.typography
    ? theme.typography.disabledText
    : themeLight.typography.disabledText};
      background: ${({ theme }) => theme.componentColors
    ? theme.componentColors.cta.disabled
    : themeLight.componentColors.cta.disabled};
    }
  }
`;
function specificVariantStyles(variant) {
    switch (variant) {
        case 'signature':
            return css `
        &${notDisabled} {
          background: ${({ theme }) => theme.componentColors
                ? theme.componentColors.cta.primary
                : themeLight.componentColors.cta.primary};
          color: ${({ theme }) => theme.typography
                ? theme.typography.brandText
                : themeLight.typography.brandText};
        }
        &:visited${notDisabled} {
          color: ${({ theme }) => theme.typography
                ? theme.typography.brandText
                : themeLight.typography.brandText};
        }
        &:hover${notDisabled}, &:focus${notDisabled} {
          background: ${({ theme }) => theme.componentColors
                ? theme.componentColors.cta.primaryHover
                : themeLight.componentColors.cta.primaryHover};
          color: ${({ theme }) => theme.typography
                ? theme.typography.brandText
                : themeLight.typography.brandText};
        }
      `;
        case 'secondary':
            return css `
        &${notDisabled} {
          background: ${({ theme }) => theme.componentColors
                ? theme.componentColors.cta.secondary
                : themeLight.componentColors.cta.secondary};
          color: ${({ theme }) => theme.typography
                ? theme.typography.brandText
                : themeLight.typography.brandText};
        }
        &:visited${notDisabled} {
          color: ${({ theme }) => theme.typography
                ? theme.typography.brandText
                : themeLight.typography.brandText};
        }
        &:hover${notDisabled}, &:focus${notDisabled} {
          background: ${({ theme }) => theme.componentColors
                ? theme.componentColors.cta.secondaryHover
                : themeLight.componentColors.cta.secondaryHover};
          color: ${({ theme }) => theme.typography
                ? theme.typography.brandText
                : themeLight.typography.brandText};
        }
      `;
        case 'outline':
            return css `
        padding: 0 calc(${spacing.space24} - 1px);
        border: 1px solid;
        border-color: ${({ theme }) => theme.border ? theme.border.heavy : themeLight.border.heavy};

        &${notDisabled} {
          background: 'transparent';
          color: ${({ theme }) => theme.typography
                ? theme.typography.defaultText
                : themeLight.typography.defaultText};
        }
        &:visited${notDisabled} {
          color: ${({ theme }) => theme.typography
                ? theme.typography.defaultText
                : themeLight.typography.defaultText};
        }
        &:hover${notDisabled}, &:focus${notDisabled} {
          border-color: ${({ theme }) => theme.typography
                ? theme.typography.defaultText
                : themeLight.typography.defaultText};
          color: ${({ theme }) => theme.typography
                ? theme.typography.defaultText
                : themeLight.typography.defaultText};
        }
      `;
        case 'spruce':
            return css `
        &${notDisabled} {
          background: ${({ theme }) => theme.componentColors
                ? theme.componentColors.cta.secondaryInverted
                : themeLight.componentColors.cta.secondaryInverted};
          color: ${({ theme }) => theme.typography
                ? theme.typography.brandTextInverted
                : themeLight.typography.brandTextInverted};
        }
        &:visited${notDisabled} {
          color: ${({ theme }) => theme.typography
                ? theme.typography.brandTextInverted
                : themeLight.typography.brandTextInverted};
        }
        &:hover${notDisabled}, &:focus${notDisabled} {
          background: ${({ theme }) => theme.componentColors
                ? theme.componentColors.cta.secondaryInvertedHover
                : themeLight.componentColors.cta.secondaryInvertedHover};
          color: ${({ theme }) => theme.typography
                ? theme.typography.brandText
                : themeLight.typography.brandText};
        }
      `;
        case 'outline-inverted':
            return css `
        padding: 0 calc(${spacing.space24} - 1px);
        border: 1px solid;
        border-color: ${({ theme }) => theme.border
                ? theme.border.heavyInverted
                : themeLight.border.heavyInverted};

        &${notDisabled} {
          background: 'transparent';
          color: ${({ theme }) => theme.typography
                ? theme.typography.defaultTextInverted
                : themeLight.typography.defaultTextInverted};
        }
        &:visited${notDisabled} {
          color: ${({ theme }) => theme.typography
                ? theme.typography.defaultTextInverted
                : themeLight.typography.defaultTextInverted};
        }
        &:hover${notDisabled}, &:focus${notDisabled} {
          border-color: ${({ theme }) => theme.typography
                ? theme.border.defaultInverted
                : themeLight.border.defaultInverted};
          color: ${({ theme }) => theme.typography
                ? theme.typography.defaultTextInverted
                : themeLight.typography.defaultTextInverted};
        }
      `;
        default:
            return null;
    }
}
function variantStyles(variant, isLoading) {
    if (isLoading) {
        const commonLoadingStyles = css `
      background: ${({ theme }) => theme.componentColors
            ? theme.componentColors.cta.disabled
            : themeLight.componentColors.cta.disabled};
      color: ${({ theme }) => theme.typography
            ? theme.typography.disabledText
            : themeLight.typography.disabledText};
    `;
        const loadingStyles = css `
      cursor: default;
      border: none;
      ${commonLoadingStyles}

      &:hover,
      &:focus,
      &:visited {
        ${commonLoadingStyles}
      }
    `;
        return css `
      ${loadingStyles}

      &[disabled],
      &[data-disabled],
      &:disabled {
        ${loadingStyles}
      }
    `;
    }
    const commonVariantStyle = !!variant
        ? css `
        &:hover {
          text-decoration: none;
        }
      `
        : null;
    return [commonVariantStyle, specificVariantStyles(variant)];
}
function sizeStyles(size) {
    switch (size) {
        case 'small':
            return css `
        height: ${spacing.space40};
        padding: 0 ${spacing.space24};
      `;
        case 'medium':
            return css `
        height: ${spacing.space48};
        padding: 0 ${spacing.space24};
      `;
        case 'large':
            return css `
        height: ${spacing.space56};
        padding: 0 ${spacing.space24};
      `;
        default:
            return null;
    }
}
function widthStyles(width) {
    switch (width) {
        case 'half':
            return css `
        width: 50%;
      `;
        case 'full':
            return css `
        width: 100%;
      `;
        default:
            return null;
    }
}
function toggleStyles(props) {
    const variant = props.variant || defaultState.variant;
    const size = props.size || defaultState.size;
    const width = props.width || defaultState.width;
    const isLoading = props.isLoading || defaultState.isLoading;
    return [
        variantStyles(variant, isLoading),
        sizeStyles(size),
        widthStyles(width),
    ];
}
const StyledButton = styled.button `
  ${commonStyles}
  ${(props) => toggleStyles(props)}
`;
const StyledAnchor = styled.a `
  ${commonStyles}
  ${(props) => toggleStyles(props)}
`;
const StyledPlaceholderDiv = styled.div `
  ${commonStyles}
  cursor: default;

  ${(props) => toggleStyles(props)}
`;
const LoadingButton = styled.div `
  display: flex;
  align-items: center;

  > div {
    margin-right: ${spacing.space8};
  }
`;
const StyledText = styled(Text) `
  display: flex;
  align-items: center;

  svg {
    font-size: ${icons.s};
  }
`;
const ButtonContent = ({ isLoading, loadingLabel, size, children, }) => {
    const computedVariant = size === 'small' ? TextVariant.ButtonSmall : TextVariant.Button;
    return isLoading ? (<LoadingButton>
      <Loader variant="neutral" size="small"/>
      <Text variant={computedVariant}>{loadingLabel || 'Laster...'}</Text>
    </LoadingButton>) : (<StyledText variant={computedVariant}>{children}</StyledText>);
};
export const Button = forwardRef(function ({ isLoading = defaultState.isLoading, loadingLabel = defaultState.loadingLabel, disabled = defaultState.disabled, size = defaultState.size, variant = defaultState.variant, width = defaultState.width, isNotClickable = defaultState.isNotClickable, text, children = text, ...rest }, ref) {
    const isDisabled = disabled || isLoading;
    if (isNotClickable) {
        return (<StyledPlaceholderDiv isLoading={isLoading} size={size} variant={variant} width={width} disabled={disabled}>
        <ButtonContent isLoading={isLoading} loadingLabel={loadingLabel} size={size}>
          {children}
        </ButtonContent>
      </StyledPlaceholderDiv>);
    }
    if (rest.href) {
        const { href, onClick, ...other } = rest;
        return (<StyledAnchor ref={ref} href={!isDisabled ? href : undefined} onClick={(e) => {
                if (onClick && !isDisabled)
                    onClick(e);
            }} data-disabled={isDisabled ? true : undefined} isLoading={isLoading} size={size} variant={variant} width={width} {...other}>
        <ButtonContent isLoading={isLoading} loadingLabel={loadingLabel} size={size}>
          {children}
        </ButtonContent>
      </StyledAnchor>);
    }
    if (rest.onClick) {
        const { type, onClick, ...other } = rest;
        return (<StyledButton ref={ref} type={type} onClick={(e) => {
                if (onClick)
                    onClick(e);
            }} isLoading={isLoading} disabled={isDisabled} size={size} variant={variant} width={width} {...other}>
        <ButtonContent isLoading={isLoading} loadingLabel={loadingLabel} size={size}>
          {children}
        </ButtonContent>
      </StyledButton>);
    }
    const { type, onClick, ...other } = rest;
    return (<StyledButton ref={ref} type={type} onClick={(e) => {
            if (onClick)
                onClick(e);
        }} isLoading={isLoading} disabled={isDisabled} size={size} variant={variant} width={width} {...other}>
      <ButtonContent isLoading={isLoading} loadingLabel={loadingLabel} size={size}>
        {children}
      </ButtonContent>
    </StyledButton>);
});
export default Button;
